import {
  ArrowRight,
  Bell,
  Check,
  ChevronDown,
  CircleAlert,
  Clock,
  Copy,
  Database,
  ExternalLink,
  Info,
  Link2,
  LoaderCircle,
  RefreshCw,
  ScrollText,
  Sparkles,
  WifiOff,
  X,
} from 'lucide-react';
import { lazy, Suspense, useMemo, useState, type ReactNode, type Ref } from 'react';
import type { SummarizeStage, SummaryLang, TranscriptMode, VideoMeta } from '../../shared/api';
import { SUMMARY_LANGS } from '../../shared/lang';
import { youTubeThumbnailUrl, youTubeWatchUrl } from '../../shared/youtube-url';
import { formatDate, formatDuration, formatElapsed } from '../lib/format';
import { useNow } from '../lib/hooks';
import { splitSummaryHeading, type SummaryHeading } from '../lib/markdown';
import { BY_WORDS, ERROR_TEXTS, LANG_NAMES, RECONNECT_TEXTS, STAGE_LABELS } from '../lib/texts';
import { stageIndex, type Job, type JobError, type StartOptions } from '../lib/useSummaryJob';
import { CopyButton } from './CopyButton';
import { Thumbnail } from './Thumbnail';
import { TranscriptView } from './TranscriptView';

const MarkdownView = lazy(() => import('./MarkdownView'));

export type ErrorAction = 'connect-codex' | 'sign-in' | 'new-link';

export interface ResultCardProps {
  job: Job;
  signedIn: boolean;
  /** How the transcript view fetches a transcript that is not loaded yet. */
  transcriptMode: TranscriptMode;
  onCancel: () => void;
  onRetry: (overrides?: Partial<StartOptions>) => void;
  onRegenerate: () => void;
  /** Show the summary in another language. */
  onChangeLang: (lang: SummaryLang) => void;
  onAction: (action: ErrorAction) => void;
  onCopyMarkdown: () => Promise<boolean>;
  onCopyLink: () => Promise<boolean>;
  /** Offered while the summary is generating: ask for permission to notify when it is ready. */
  onNotify?: (() => void) | undefined;
  /** Closes this video's tab and moves on; `next` is the title of the tab shown afterwards (null: start page). */
  closeTab?: { onClose: () => void; next: string | null } | undefined;
  ref?: Ref<HTMLElement>;
}

const STAGES: SummarizeStage[] = ['cache', 'transcript', 'summarizing', 'writing'];

/**
 * Layout: a header with thumbnail, facts and actions, then the summary itself. The summary
 * Markdown starts with `# <Titel>` + `### von <Creator>`; that heading is the only place where
 * title and channel appear (before it exists, oEmbed/meta values stand in for it in the same spot).
 */
export function ResultCard({
  job,
  signedIn,
  transcriptMode,
  onCancel,
  onRetry,
  onRegenerate,
  onChangeLang,
  onAction,
  onCopyMarkdown,
  onCopyLink,
  onNotify,
  closeTab,
  ref,
}: ResultCardProps) {
  const running = job.status === 'running';
  const youtubeUrl = youTubeWatchUrl(job.videoId);
  const split = useMemo(() => splitSummaryHeading(job.markdown), [job.markdown]);
  const body = split ? split.body : job.markdown;
  // Summary or original transcript; every other video starts with the summary.
  const [shown, setShown] = useState<{ videoId: string; view: View }>({ videoId: job.videoId, view: 'summary' });
  const canSwitch = job.status === 'done' || running;
  const view = canSwitch && shown.videoId === job.videoId ? shown.view : 'summary';

  return (
    <article ref={ref} className="result glass" aria-labelledby="result-title" aria-busy={running} tabIndex={-1}>
      <header className="result__head">
        <a className="result__thumb-link" href={youtubeUrl} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden="true">
          <Thumbnail className="result__thumb" src={youTubeThumbnailUrl(job.videoId)} width={480} height={360} />
        </a>
        <Facts job={job} youtubeUrl={youtubeUrl} onChangeLang={onChangeLang} />
        {job.status === 'done' && (
          <Actions onCopyMarkdown={onCopyMarkdown} onCopyLink={onCopyLink} onRegenerate={onRegenerate} />
        )}
        {running && <Progress job={job} onCancel={onCancel} onNotify={onNotify} />}
        <Heading heading={split} meta={job.meta} lang={job.lang} loading={running} videoId={job.videoId} />
      </header>

      {canSwitch && <ViewSwitch view={view} onChange={(next) => setShown({ videoId: job.videoId, view: next })} />}

      {job.notice && view === 'summary' && (
        <p className="result__notice">
          <Info size={15} aria-hidden="true" />
          {job.notice}
        </p>
      )}

      {job.status === 'error' && job.error && (
        <ErrorPanel error={job.error} job={job} signedIn={signedIn} onRetry={onRetry} onAction={onAction} youtubeUrl={youtubeUrl} />
      )}

      {job.status === 'cancelled' && (
        <div className="notice">
          <p>Die Zusammenfassung wurde abgebrochen.</p>
          <button type="button" className="btn btn--secondary" onClick={() => onRetry()}>
            <RefreshCw size={18} aria-hidden="true" />
            Erneut starten
          </button>
        </div>
      )}

      {view === 'transcript' ? (
        <TranscriptView videoId={job.videoId} lang={job.lang} mode={transcriptMode} />
      ) : body.trim() ? (
        <Suspense fallback={<SkeletonText />}>
          <MarkdownView markdown={body} streaming={running} />
        </Suspense>
      ) : (
        running && <SkeletonText />
      )}

      {closeTab && <CloseTabBar {...closeTab} done={job.status === 'done'} />}
    </article>
  );
}

type View = 'summary' | 'transcript';

const VIEWS = [
  ['summary', 'Zusammenfassung', Sparkles],
  ['transcript', 'Transkript', ScrollText],
] as const;

/** Switches between the summary and the video's original transcript. */
function ViewSwitch({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  return (
    <div className="view-switch" role="group" aria-label="Ansicht">
      {VIEWS.map(([value, label, Icon]) => (
        <button
          key={value}
          type="button"
          aria-pressed={view === value}
          className={`view-switch__option${view === value ? ' is-selected' : ''}`}
          onClick={() => onChange(value)}
        >
          <Icon size={16} aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}

/** End of the summary: close the tab (it was read) and continue with the next one. */
function CloseTabBar({ onClose, next, done }: { onClose: () => void; next: string | null; done: boolean }) {
  return (
    <div className="result__footer">
      <button type="button" className={`btn ${done ? 'btn--primary' : 'btn--secondary'} result__close`} onClick={onClose}>
        {done ? <Check size={19} aria-hidden="true" /> : <X size={19} aria-hidden="true" />}
        {done ? 'Gelesen – Tab schließen' : 'Tab schließen'}
      </button>
      <p className="result__next">
        {next ? (
          <>
            <span className="result__next-label">Danach:</span> <span className="result__next-title">{next}</span>
          </>
        ) : (
          <span className="result__next-label">Danach geht es zur Startseite.</span>
        )}
      </p>
    </div>
  );
}

/** Title + "von <Creator>": from the summary heading, else from the video metadata. */
function Heading({
  heading,
  meta,
  lang,
  loading,
  videoId,
}: {
  heading: SummaryHeading | null;
  meta: Partial<VideoMeta> | undefined;
  lang: SummaryLang | undefined;
  loading: boolean;
  videoId: string;
}) {
  const title = heading?.title ?? meta?.title;
  const by = BY_WORDS[lang ?? 'de'];
  const byline = heading ? heading.subtitle : meta?.channel ? `${by} ${meta.channel}` : undefined;

  let bylineContent: ReactNode = byline;
  const match = byline ? /^(von|by|de|van)\s+(.+)$/i.exec(byline) : null;
  if (match) {
    const name = match[2]!;
    bylineContent = (
      <>
        <span className="summary-head__by">{match[1]}</span>{' '}
        {meta?.channelUrl && meta.channel === name ? (
          <a href={meta.channelUrl} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        ) : (
          name
        )}
      </>
    );
  }

  return (
    <div className={`summary-head${heading ? '' : ' is-placeholder'}`}>
      <h1 id="result-title" className="summary-head__title">
        {title ??
          (loading ? (
            <>
              <span className="sr-only">Video wird geladen</span>
              <span className="skeleton skeleton--title" aria-hidden="true" />
              <span className="skeleton skeleton--title skeleton--short" aria-hidden="true" />
            </>
          ) : (
            'YouTube-Video'
          ))}
      </h1>
      {byline ? (
        <p className="summary-head__byline">{bylineContent}</p>
      ) : loading ? (
        <p className="summary-head__byline">
          <span className="skeleton skeleton--byline" aria-hidden="true" />
        </p>
      ) : !title ? (
        <p className="summary-head__byline">
          <span className="video-id">{videoId}</span>
        </p>
      ) : null}
    </div>
  );
}

/** "auf Englisch" – in a finished summary a native select to switch the language. */
function LangFact({ job, onChangeLang }: { job: Job; onChangeLang: (lang: SummaryLang) => void }) {
  const lang = job.lang;
  if (!lang) return null;
  if (job.status !== 'done') return <span>auf {LANG_NAMES[lang]}</span>;
  return (
    <span className="lang-switch">
      <select
        className="lang-switch__select"
        value={lang}
        aria-label="Sprache der Zusammenfassung"
        title="In anderer Sprache lesen"
        onChange={(event) => onChangeLang(event.target.value as SummaryLang)}
      >
        {SUMMARY_LANGS.map((option) => (
          <option key={option} value={option}>
            auf {LANG_NAMES[option]}
          </option>
        ))}
      </select>
      <ChevronDown size={13} aria-hidden="true" />
    </span>
  );
}

function Facts({ job, youtubeUrl, onChangeLang }: { job: Job; youtubeUrl: string; onChangeLang: (lang: SummaryLang) => void }) {
  const facts: ReactNode[] = [];
  const duration = job.meta?.durationSeconds;
  if (duration) {
    facts.push(
      <span key="duration" title="Länge des Videos">
        <Clock size={13} aria-hidden="true" />
        <span className="sr-only">Länge: </span>
        {formatDuration(duration)}
      </span>,
    );
  }
  if (job.status === 'done') {
    if (job.summary?.createdAt) facts.push(<span key="date">{formatDate(job.summary.createdAt)}</span>);
    facts.push(<LangFact key="lang" job={job} onChangeLang={onChangeLang} />);
    if (job.source === 'server-cache' || job.summary?.cached) {
      facts.push(
        <span key="cache" title="Diese Zusammenfassung wurde bereits früher erstellt und sofort geladen.">
          <Database size={13} aria-hidden="true" />
          aus dem Cache
        </span>,
      );
    } else if (job.source === 'history') {
      facts.push(
        <span key="history" title="Aus deinem Verlauf in diesem Browser.">
          <Clock size={13} aria-hidden="true" />
          aus dem Verlauf
        </span>,
      );
    } else if (job.endedAt) {
      facts.push(<span key="time">erstellt in {formatElapsed(job.endedAt - job.startedAt)}</span>);
    }
  } else if (job.lang) {
    facts.push(<LangFact key="lang" job={job} onChangeLang={onChangeLang} />);
  }
  facts.push(
    <a key="yt" href={youtubeUrl} target="_blank" rel="noopener noreferrer">
      Auf YouTube ansehen
      <ExternalLink size={12} aria-hidden="true" />
    </a>,
  );
  return (
    <div className="result__facts">
      <p className="result__facts-inner">{facts}</p>
    </div>
  );
}

function Actions({
  onCopyMarkdown,
  onCopyLink,
  onRegenerate,
}: {
  onCopyMarkdown: () => Promise<boolean>;
  onCopyLink: () => Promise<boolean>;
  onRegenerate: () => void;
}) {
  return (
    <div className="actions" role="group" aria-label="Aktionen">
      <CopyButton
        label="Markdown kopieren"
        shortLabel="Markdown"
        icon={<Copy size={17} aria-hidden="true" />}
        onCopy={onCopyMarkdown}
      />
      <CopyButton label="Link kopieren" icon={<Link2 size={17} aria-hidden="true" />} onCopy={onCopyLink} />
      <button type="button" className="btn btn--secondary btn--sm" onClick={onRegenerate}>
        <RefreshCw size={17} aria-hidden="true" />
        <span>Neu generieren</span>
      </button>
    </div>
  );
}

function Progress({ job, onCancel, onNotify }: { job: Job; onCancel: () => void; onNotify?: (() => void) | undefined }) {
  const now = useNow(true);
  const current = stageIndex(job.stage);
  const statusText = job.reconnecting
    ? RECONNECT_TEXTS.reconnecting
    : job.waiting
      ? WAITING_NOTES[job.waiting]
      : `${STAGE_LABELS[job.stage]} …`;
  const note = job.reconnecting
    ? RECONNECT_TEXTS.reconnecting
    : job.waiting
      ? WAITING_NOTES[job.waiting]
      : (job.progress && job.stage === 'summarizing' ? plainNote(job.progress) : '') || job.stageMessage || defaultNote(job);
  return (
    <div className="progress">
      <div className="progress__bar" aria-hidden="true">
        {STAGES.map((stage, index) => (
          <span key={stage} className={index < current ? 'is-done' : index === current ? 'is-active' : ''} />
        ))}
      </div>
      <div className="steps-clip">
        <ol className="steps" aria-label="Fortschritt">
          {STAGES.map((stage, index) => {
            const state = index < current ? 'done' : index === current ? 'active' : 'todo';
            return (
              <li key={stage} className={`step is-${state}`} aria-current={state === 'active' ? 'step' : undefined}>
                <span className="step__icon" aria-hidden="true">
                  {state === 'done' ? (
                    <Check size={14} strokeWidth={3} />
                  ) : state === 'active' ? (
                    <LoaderCircle size={17} className="spin" />
                  ) : (
                    <span className="step__dot" />
                  )}
                </span>
                <span className="step__label">
                  {STAGE_LABELS[stage]}
                  <span className="sr-only">{state === 'done' ? ' (erledigt)' : state === 'active' ? ' (läuft)' : ''}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="progress__footer">
        <p
          key={note}
          className={`progress__note${job.reconnecting ? ' progress__note--reconnecting' : ''}`}
          title={note || undefined}
        >
          {job.reconnecting && <WifiOff size={14} aria-hidden="true" className="progress__note-icon" />}
          {note}
        </p>
        <span className="progress__elapsed" title="Verstrichene Zeit">
          <Clock size={15} aria-hidden="true" />
          <span className="sr-only">Verstrichene Zeit: </span>
          {formatElapsed(now - job.startedAt)}
        </span>
        <button type="button" className="btn btn--secondary btn--sm progress__cancel" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
      {onNotify && (
        <button type="button" className="link-btn progress__notify" onClick={onNotify}>
          <Bell size={15} aria-hidden="true" />
          Benachrichtigen, wenn bereit
        </button>
      )}
      <p className="sr-only" role="status" aria-live="polite">
        {statusText}
      </p>
    </div>
  );
}

const WAITING_NOTES = {
  slot: 'Wartet, bis eine andere Zusammenfassung fertig ist …',
} as const;

function defaultNote(job: Job): string {
  switch (job.stage) {
    case 'cache':
      return 'Prüfe, ob es schon eine Zusammenfassung gibt …';
    case 'transcript':
      return job.mode === 'client' ? 'Lade die Untertitel direkt in deinem Browser …' : 'Lade die Untertitel von YouTube …';
    case 'summarizing':
      return 'ChatGPT liest das Transkript und plant die Zusammenfassung …';
    case 'writing':
      return 'ChatGPT schreibt die Zusammenfassung – du kannst schon lesen.';
  }
}

/** A model progress note as plain text ("**Analysing the transcript**" → "Analysing the transcript"). */
function plainNote(text: string): string {
  return text
    .replace(/\*\*|__|`/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function SkeletonText() {
  return (
    <div className="skeleton-text" aria-hidden="true">
      <span className="skeleton skeleton--h" />
      <span className="skeleton skeleton--line" />
      <span className="skeleton skeleton--line" />
      <span className="skeleton skeleton--line skeleton--short" />
      <span className="skeleton skeleton--h" />
      <span className="skeleton skeleton--line" />
      <span className="skeleton skeleton--line skeleton--mid" />
    </div>
  );
}

interface ErrorButton {
  label: string;
  onClick: () => void;
  primary?: boolean;
  href?: string;
  icon?: ReactNode;
}

function ErrorPanel({
  error,
  job,
  signedIn,
  onRetry,
  onAction,
  youtubeUrl,
}: {
  error: JobError;
  job: Job;
  signedIn: boolean;
  onRetry: (overrides?: Partial<StartOptions>) => void;
  onAction: (action: ErrorAction) => void;
  youtubeUrl: string;
}) {
  const text = ERROR_TEXTS[error.code];
  const loadInBrowser: ErrorButton = { label: 'Im Browser laden', onClick: () => onRetry({ mode: 'client', skipCache: true }) };
  const loadOnServer: ErrorButton = { label: 'Über den Server laden', onClick: () => onRetry({ mode: 'server', skipCache: true }) };
  const retry: ErrorButton = { label: 'Erneut versuchen', onClick: () => onRetry(), icon: <RefreshCw size={18} aria-hidden="true" /> };
  const buttons: ErrorButton[] = [];
  switch (error.code) {
    case 'codex_auth_required':
      buttons.push({ label: 'Mit ChatGPT verbinden', onClick: () => onAction('connect-codex'), primary: true });
      break;
    case 'codex_auth_invalid':
      buttons.push({ label: 'Neu verbinden', onClick: () => onAction('connect-codex'), primary: true });
      break;
    case 'codex_quota':
      buttons.push({ ...retry, primary: true }, { label: 'Anderes Konto verbinden', onClick: () => onAction('connect-codex') });
      break;
    case 'login_required':
      buttons.push({ label: 'Anmelden', onClick: () => onAction('sign-in'), primary: true }, loadInBrowser);
      break;
    case 'youtube_blocked':
      buttons.push({ ...loadInBrowser, primary: true }, retry);
      break;
    case 'transcript_unavailable':
      if (job.mode === 'server') buttons.push({ ...loadInBrowser, label: 'Im Browser versuchen' });
      buttons.push(
        { label: 'Auf YouTube öffnen', onClick: () => {}, href: youtubeUrl, icon: <ExternalLink size={18} aria-hidden="true" /> },
        { label: 'Anderes Video', onClick: () => onAction('new-link'), primary: true },
      );
      break;
    case 'transcript_browser':
    case 'transcript_blocked':
      buttons.push({ ...retry, primary: true });
      if (signedIn && !job.serverBlocked) buttons.push(loadOnServer);
      break;
    case 'video_not_found':
      buttons.push({ label: 'Anderen Link eingeben', onClick: () => onAction('new-link'), primary: true });
      break;
    default:
      buttons.push({ ...retry, primary: true });
  }

  // "Connect / sign in to continue" is a next step, not a failure: neutral styling.
  const info = error.code === 'codex_auth_required' || error.code === 'login_required';
  const Icon = info ? Sparkles : CircleAlert;
  return (
    <div className={`error-panel${info ? ' error-panel--info' : ''}`} role="alert">
      <Icon className="error-panel__icon" size={24} aria-hidden="true" />
      <div className="error-panel__body">
        <h2 className="error-panel__title">{text.title}</h2>
        <p className="error-panel__message">{error.message ?? text.message}</p>
        <div className="error-panel__actions">
          {buttons.map((button) =>
            button.href ? (
              <a
                key={button.label}
                className={`btn ${button.primary ? 'btn--primary' : 'btn--secondary'}`}
                href={button.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {button.icon}
                {button.label}
              </a>
            ) : (
              <button
                key={button.label}
                type="button"
                className={`btn ${button.primary ? 'btn--primary' : 'btn--secondary'}`}
                onClick={button.onClick}
              >
                {button.icon}
                {button.label}
                {button.primary && !button.icon && <ArrowRight size={18} aria-hidden="true" />}
              </button>
            ),
          )}
        </div>
        {error.detail && (
          <details className="error-panel__details">
            <summary>Technische Details</summary>
            <code>
              {error.code}: {error.detail}
            </code>
          </details>
        )}
      </div>
    </div>
  );
}
